import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type UnavailableDocumentJSON = z.infer<typeof UnavailableDocumentSchema.UnavailableDocumentJSONZ>;
export type UnavailableDocumentParams = z.infer<typeof UnavailableDocumentSchema.UnavailableDocumentZ>;

export default class UnavailableDocumentSchema {
  public static readonly UnavailableDocumentJSONZ = z.object({
    id: z.uuid(),
    issue: z.string(),
  });
  public static readonly UnavailableDocumentZ = z.object({
    id: z.instanceof(Guid),
    issue: z.string(),
  });

  public static validateJSON(values: unknown): UnavailableDocumentParams {
    return parseOrThrow(UnavailableDocumentSchema.UnavailableDocumentZ, values, {
      logError: true,
      schemaName: "UnavailableDocumentSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is UnavailableDocumentParams {
    try {
      UnavailableDocumentSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: UnavailableDocumentJSON): UnavailableDocumentParams {
    const parsed = parseOrThrow(UnavailableDocumentSchema.UnavailableDocumentJSONZ, data, {
      logError: true,
      schemaName: "UnavailableDocumentSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return UnavailableDocumentSchema.codec.decode(parsed);
  }

  public static serialize(document: UnavailableDocumentParams): UnavailableDocumentJSON {
    const parsed = UnavailableDocumentSchema.validateJSON(document);
    return UnavailableDocumentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UnavailableDocumentSchema.UnavailableDocumentJSONZ,
    UnavailableDocumentSchema.UnavailableDocumentZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        issue: data.issue,
      }),
      encode: (document) => ({
        id: document.id.toJSON(),
        issue: document.issue,
      }),
    }
  );
}
